import React from 'react';
import { Archive, ClockCounterClockwise, File, FilePdf, FileImage, PenNib, Sparkle, TrayArrowDown, Warning } from '@phosphor-icons/react';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { HAUPTORDNER, ORDNER_FARBE } from '../../../../lib/dokumente/ordner';
import type { DokumenteKopf, ZuletztDatei } from '../../../../lib/dokumente/useDokumenteKopf';

const KARTE: React.CSSProperties = { borderRadius: 26, padding: 16, gap: 8, minHeight: 150, textAlign: 'left', border: 'none', color: 'var(--ink)' };
const TITEL: React.CSSProperties = { gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--acc)' };

export const dateiIcon = (name: string | null) => (/\.pdf$/i.test(name ?? '') ? FilePdf : /\.(png|jpe?g|gif|webp|heic)$/i.test(name ?? '') ? FileImage : File);

/** Widgets der Dokumente (Handoff Dokumente §6.1, Screens 01/20/31). */
export function DokumenteWidgets({ k, onAblage, onSignieren, onAkten, onDatei, onZuletzt }: {
  k: DokumenteKopf;
  onAblage: () => void;
  onSignieren: () => void;
  onAkten: () => void;
  onDatei: (d: ZuletztDatei) => void;
  /** #609: große Liste „Zuletzt hinzugefügt“ öffnen */
  onZuletzt?: () => void;
}) {
  const s = k.signatur;
  const umfang = 2 * Math.PI * 50;
  const ordnerMitDateien = HAUPTORDNER.filter(o => k.akten.je[o] > 0);
  return (
    <section aria-label="Übersicht Dokumente" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 14 }}>
      <button type="button" onClick={onAblage} className="immo-glas immo-hover-widget flex flex-col cursor-pointer" style={KARTE}>
        <span className="flex items-center" style={TITEL}><TrayArrowDown weight="fill" size={15} />Zur Ablage</span>
        <span className="immo-zahl" style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, marginTop: 'auto' }}>{k.ablage.offen.toLocaleString('de-DE')}</span>
        <span style={{ fontSize: 12, color: 'var(--ink2)' }}>Anhänge aus E-Mails warten auf Freigabe</span>
        {k.ablage.hoch > 0 && (
          <span className="inline-flex items-center self-start" style={{ gap: 5, height: 24, padding: '0 9px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: 'var(--ok-soft)', color: 'var(--ok)' }}>
            <Sparkle weight="fill" size={12} />{k.ablage.hoch.toLocaleString('de-DE')} mit hoher Sicherheit
          </span>
        )}
      </button>

      <button type="button" onClick={onSignieren} className="immo-glas immo-hover-widget flex flex-col cursor-pointer" style={KARTE}>
        <span className="flex items-center" style={TITEL}><PenNib weight="fill" size={15} />Signaturen</span>
        <div className="flex flex-1 items-center" style={{ gap: 12 }}>
          <div className="relative shrink-0" style={{ width: 70, height: 70 }}>
            <svg width={70} height={70} viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }} aria-hidden>
              <circle cx="60" cy="60" r="50" fill="none" stroke="var(--fill3)" strokeWidth="14" />
              {s.anteil > 0 && <circle cx="60" cy="60" r="50" fill="none" stroke="var(--info)" strokeWidth="14" strokeLinecap="round" strokeDasharray={`${umfang * s.anteil} ${umfang}`} />}
            </svg>
            <span className="immo-zahl absolute inset-0 flex items-center justify-center" style={{ fontSize: 20, fontWeight: 800 }}>{s.wartend}</span>
          </div>
          <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>warten auf Unterschrift</span>
            <span style={{ fontSize: 12, color: "var(--ink2)", fontVariantNumeric: "tabular-nums" }}>{s.signiert} von {s.gesamt} Unterschriften da</span>
          </div>
        </div>
        {s.nichtAbgelegt > 0 && (
          <span className="inline-flex items-center self-start" style={{ gap: 5, height: 24, padding: '0 9px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: 'var(--warn-soft)', color: 'var(--warn)' }}>
            <Archive weight="fill" size={12} />{s.nichtAbgelegt} fertig, noch nicht abgelegt
          </span>
        )}
      </button>

      <button type="button" onClick={onAkten} className="immo-glas immo-hover-widget flex flex-col cursor-pointer" style={KARTE}>
        <span className="flex items-center" style={TITEL}><Archive weight="fill" size={15} />Aktenschrank</span>
        <span className="immo-zahl" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', marginTop: 'auto' }}>{k.akten.dateien.toLocaleString('de-DE')} Dateien</span>
        <span style={{ fontSize: 12, color: 'var(--ink2)' }}>in {k.akten.objekte} Objekt-Ordnern</span>
        {k.akten.dateien > 0 && (
          <div className="flex w-full" style={{ height: 6, gap: 3 }} aria-hidden>
            {ordnerMitDateien.map(o => <span key={o} title={`${o} · ${k.akten.je[o]} Dateien`} style={{ flexGrow: k.akten.je[o], borderRadius: 3, background: ORDNER_FARBE[o].fg }} />)}
            {k.akten.sonstige > 0 && <span title={`ohne Hauptordner-Angabe · ${k.akten.sonstige} Dateien`} style={{ flexGrow: k.akten.sonstige, borderRadius: 3, background: 'var(--fill3)' }} />}
          </div>
        )}
        {k.akten.ohneOrdner > 0 && (
          <span className="flex items-center" style={{ gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--warn)' }}>
            <Warning weight="fill" size={12} />{k.akten.ohneOrdner} {k.akten.ohneOrdner === 1 ? 'Objekt' : 'Objekte'} ohne Drive-Ordner
          </span>
        )}
      </button>

      <div className="immo-glas flex flex-col" style={KARTE}>
        <span className="flex items-center justify-between" style={{ gap: 6 }}>
          <span className="flex items-center" style={TITEL}><ClockCounterClockwise weight="fill" size={15} />Zuletzt hinzugefügt</span>
          {onZuletzt && (
            <button type="button" onClick={onZuletzt} className="immo-hover-flaeche cursor-pointer" data-tip="Alle neuen Dateien in einer großen Liste mit Filtern"
              style={{ border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 12, fontWeight: 800, padding: '6px 10px', borderRadius: 10, minHeight: 32 }}>
              Alle anzeigen
            </button>
          )}
        </span>
        <div className="flex flex-col" style={{ gap: 4, marginTop: 'auto' }}>
          {k.zuletzt.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Noch keine Dateien im Aktenschrank</span>}
          {k.zuletzt.map(d => {
            const IconKomp = dateiIcon(d.datei_name);
            const name = d.datei_name || d.titel || 'Datei';
            return (
              <button key={d.id} type="button" onClick={() => onDatei(d)} className="immo-hover-flaeche flex items-center min-w-0 cursor-pointer text-left"
                style={{ gap: 8, padding: '6px 8px', borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink)', minHeight: 44 }}>
                <IconKomp size={18} color="var(--acc)" style={{ flexShrink: 0 }} />
                <span className="flex flex-col min-w-0">
                  <span className="truncate" style={{ fontSize: 12, fontWeight: 700 }}>{name}</span>
                  <span className="truncate immo-zahl" style={{ fontSize: 11, color: 'var(--ink3)' }}>
                    {[d.objekt?.objektnummer, deDatum(d.created_at.slice(0, 10)), d.kategorie].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
